import {
  Briefcase,
  Users,
  FileText,
  CheckSquare,
  DollarSign,
  StickyNote,
  Search,
  ExternalLink,
} from 'lucide-react';

const FG = '#F5F1FA';
const MUTED = '#A99FB8';
const SURFACE2 = '#140C20';
const SURFACE = '#1B1029';
const VIOLET = '#6D28D9';
const VIOLET_LIGHT = '#A855F7';
const BORDER = 'rgba(255,255,255,0.08)';

const TABS = [
  { icon: FileText, label: 'Dados gerais', active: true },
  { icon: Users, label: 'Partes', active: false },
  { icon: FileText, label: 'Movimentações', active: false },
  { icon: FileText, label: 'Documentos', active: false },
  { icon: CheckSquare, label: 'Tarefas', active: false },
  { icon: DollarSign, label: 'Financeiro', active: false },
  { icon: StickyNote, label: 'Anotações', active: false },
];

export function ProcessosMockup() {
  return (
    <div
      style={{
        width: '100%',
        borderRadius: 16,
        overflow: 'hidden',
        border: `1px solid ${BORDER}`,
        background: SURFACE2,
        boxShadow:
          '0 0 0 1px rgba(109,40,217,0.15), 0 32px 80px rgba(0,0,0,0.55), 0 8px 24px rgba(109,40,217,0.12)',
        fontFamily: "'Hanken Grotesk', sans-serif",
      }}
    >
      {/* Titlebar */}
      <div
        style={{
          background: 'rgba(20,12,32,0.98)',
          borderBottom: `1px solid rgba(255,255,255,0.07)`,
          padding: '12px 18px',
          display: 'flex',
          alignItems: 'center',
          gap: 14,
        }}
      >
        <div style={{ display: 'flex', gap: 7, flexShrink: 0 }}>
          {['#FF5F56', '#FFBD2E', '#27C93F'].map((c) => (
            <span
              key={c}
              style={{
                width: 11,
                height: 11,
                borderRadius: '50%',
                background: c,
                opacity: 0.75,
                display: 'inline-block',
              }}
            />
          ))}
        </div>
        <Briefcase size={13} color={VIOLET_LIGHT} strokeWidth={2} />
        <span style={{ fontSize: 12, fontWeight: 600, color: FG }}>
          Processos
        </span>
      </div>

      {/* Layout 2 colunas */}
      <style>{`
        @media (max-width: 600px) {
          .proc-layout { flex-direction: column !important; }
          .proc-sidebar { width: 100% !important; border-right: none !important; border-bottom: 1px solid rgba(255,255,255,0.08) !important; }
        }
      `}</style>
      <div className="proc-layout" style={{ display: 'flex', minHeight: 320 }}>
        {/* Sidebar */}
        <div
          className="proc-sidebar"
          style={{
            width: 200,
            flexShrink: 0,
            borderRight: `1px solid ${BORDER}`,
            background: 'rgba(20,12,32,0.6)',
            padding: '16px 0',
            display: 'flex',
            flexDirection: 'column',
            gap: 0,
          }}
        >
          {/* Label */}
          <p
            style={{
              fontSize: 9,
              fontWeight: 700,
              letterSpacing: '0.14em',
              textTransform: 'uppercase' as const,
              color: MUTED,
              margin: '0 0 10px',
              padding: '0 14px',
            }}
          >
            PROCESSO
          </p>

          {/* CNJ */}
          <div
            style={{
              padding: '0 14px 14px',
              borderBottom: `1px solid ${BORDER}`,
            }}
          >
            <p
              style={{
                fontSize: 10,
                color: MUTED,
                margin: '0 0 2px',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              0001234-56.2025.8.21.0010
            </p>
            <p
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: FG,
                margin: '0 0 8px',
                lineHeight: 1.3,
              }}
            >
              AÇÃO DE INDENIZAÇÃO
            </p>
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                fontSize: 10,
                fontWeight: 600,
                color: '#4ADE80',
                background: 'rgba(74,222,128,0.1)',
                border: '1px solid rgba(74,222,128,0.25)',
                borderRadius: 20,
                padding: '2px 8px',
              }}
            >
              <span
                style={{
                  width: 5,
                  height: 5,
                  borderRadius: '50%',
                  background: '#4ADE80',
                  boxShadow: '0 0 6px #4ADE8099',
                  display: 'inline-block',
                }}
              />
              Ativo
            </span>
          </div>

          {/* Abas na sidebar */}
          <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 1 }}>
            {TABS.map((tab) => {
              const Icon = tab.icon;
              return (
                <div
                  key={tab.label}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 9,
                    padding: '8px 14px',
                    background: tab.active
                      ? 'rgba(109,40,217,0.18)'
                      : 'transparent',
                    borderLeft: tab.active
                      ? `2px solid ${VIOLET_LIGHT}`
                      : '2px solid transparent',
                    cursor: 'default',
                  }}
                >
                  <Icon
                    size={12}
                    color={tab.active ? VIOLET_LIGHT : MUTED}
                    strokeWidth={2}
                  />
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: tab.active ? 600 : 400,
                      color: tab.active ? FG : MUTED,
                    }}
                  >
                    {tab.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Painel direito */}
        <div style={{ flex: 1, padding: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Campos Natureza + Status */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {[
              { label: 'Natureza', value: 'Judicial' },
              { label: 'Status', value: 'Ativo' },
            ].map((f) => (
              <div key={f.label}>
                <p style={{ fontSize: 10, fontWeight: 600, color: MUTED, margin: '0 0 4px', letterSpacing: '0.05em', textTransform: 'uppercase' as const }}>
                  {f.label}
                </p>
                <div
                  style={{
                    background: SURFACE,
                    border: `1px solid ${BORDER}`,
                    borderRadius: 7,
                    padding: '7px 10px',
                    fontSize: 12,
                    color: FG,
                    fontWeight: 500,
                  }}
                >
                  {f.value}
                </div>
              </div>
            ))}
          </div>

          {/* Campo Numero CNJ com botoes */}
          <div>
            <p style={{ fontSize: 10, fontWeight: 600, color: MUTED, margin: '0 0 4px', letterSpacing: '0.05em', textTransform: 'uppercase' as const }}>
              Número CNJ
            </p>
            <div
              style={{
                background: SURFACE,
                border: `1px solid ${BORDER}`,
                borderRadius: 7,
                padding: '7px 10px',
                fontSize: 11,
                color: MUTED,
                fontVariantNumeric: 'tabular-nums',
                marginBottom: 8,
              }}
            >
              0001234-56.2025.8.21.0010
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 5,
                  fontSize: 10,
                  fontWeight: 600,
                  color: FG,
                  background: VIOLET,
                  border: 'none',
                  borderRadius: 6,
                  padding: '5px 10px',
                  cursor: 'default',
                  fontFamily: 'inherit',
                }}
              >
                <Search size={10} strokeWidth={2} />
                Buscar no CNJ
              </button>
              <button
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 5,
                  fontSize: 10,
                  fontWeight: 600,
                  color: '#4ADE80',
                  background: 'transparent',
                  border: '1px solid rgba(74,222,128,0.4)',
                  borderRadius: 6,
                  padding: '5px 10px',
                  cursor: 'default',
                  fontFamily: 'inherit',
                }}
              >
                <ExternalLink size={10} strokeWidth={2} />
                Buscar no tribunal
              </button>
            </div>
          </div>

          {/* Banner tribunal identificado */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              background: 'rgba(74,222,128,0.07)',
              border: '1px solid rgba(74,222,128,0.22)',
              borderRadius: 8,
              padding: '9px 13px',
            }}
          >
            <span
              style={{
                flexShrink: 0,
                width: 7,
                height: 7,
                borderRadius: '50%',
                background: '#4ADE80',
                boxShadow: '0 0 8px #4ADE8099',
                display: 'inline-block',
              }}
            />
            <span style={{ fontSize: 11, fontWeight: 600, color: '#4ADE80' }}>
              Tribunal identificado:
            </span>
            <span style={{ fontSize: 11, color: FG }}>TJRS (eProc)</span>
          </div>
        </div>
      </div>
    </div>
  );
}
